// services/api.js - Updated with Creative Token system and Subscription System

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Token ${token}` : '',
  };
};

// Helper function to handle API errors
export const handleAPIError = (error) => {
  if (error.response) {
    // Server responded with error status
    if (error.response.data) {
      if (typeof error.response.data === 'string') {
        return error.response.data;
      }
      if (error.response.data.error) {
        return error.response.data.error;
      }
      if (error.response.data.detail) {
        return error.response.data.detail;
      }
      // Return first error message if it's an object
      const firstError = Object.values(error.response.data)[0];
      if (Array.isArray(firstError)) {
        return firstError[0];
      }
      return firstError || 'An error occurred';
    }
    return `Error: ${error.response.status}`;
  } else if (error.request) {
    // Request was made but no response received
    return 'Network error. Please check your connection.';
  } else {
    // Something else happened
    return error.message || 'An unexpected error occurred';
  }
};

// Authentication API
export const authAPI = {
  login: async (username, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token in localStorage
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      return data;
    } catch (error) {
      throw error;
    }
  },

  signup: async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/signup/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      // Store token in localStorage
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      return data;
    } catch (error) {
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('authToken');
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/dashboard/stats/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch dashboard stats');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }
};

// User API
export const userAPI = {
  getProfile: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/profile/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch profile');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/profile/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update profile');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }
};

// Projects API
export const projectsAPI = {
  getProjects: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch projects');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  createProject: async (projectData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(projectData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create project');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  getRecentProjects: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/recent-projects/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch recent projects');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  useTokens: async (projectId, tokens) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/use-tokens/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          project_id: projectId,
          tokens: tokens
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to use tokens');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }
};

// UPDATED SHOP API - Now uses Creative Tokens
export const shopAPI = {
  getShopItems: async (category = null, search = null) => {
    try {
      let url = `${API_BASE_URL}/api/shop-items/`;
      const params = new URLSearchParams();
      
      if (category) params.append('category', category);
      if (search) params.append('search', search);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch shop items');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  getMyPurchases: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/my-purchases/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch purchases');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  downloadItem: async (purchaseId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/download/${purchaseId}/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to download item');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }
};

// UPDATED CART API - Now uses Creative Tokens
export const cartAPI = {
  getCart: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cart/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch cart');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  addToCart: async (shopItemId, quantity = 1) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cart/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          shop_item_id: shopItemId,
          quantity: quantity
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add item to cart');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  updateCartItem: async (itemId, quantity) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cart-item/${itemId}/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ quantity }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update cart item');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  removeCartItem: async (itemId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cart-item/${itemId}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to remove cart item');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  clearCart: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cart/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to clear cart');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // UPDATED: Now checkout with Creative Tokens
  checkout: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/checkout/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Checkout failed');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }
};

// NEW: Creative Token API
export const tokenAPI = {
  getTokenPackages: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/token-packages/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch token packages');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  purchaseTokens: async (packageId, paymentMethod = 'credit_card') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/purchase-tokens/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          package_id: packageId,
          payment_method: paymentMethod
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to purchase tokens');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  getTokenHistory: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/token-history/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch token history');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }
};

// NEW: Subscription API
export const subscriptionAPI = {
  // Get available subscription plans
  getPlans: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscription-plans/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch subscription plans');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // Get current user subscription
  getCurrentSubscription: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/my-subscription/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch current subscription');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // Subscribe to a plan
  subscribeToPlan: async (planId, paymentMethodId = null) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscribe/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          plan_id: planId,
          payment_method_id: paymentMethodId
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to subscribe to plan');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // Cancel subscription
  cancelSubscription: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cancel-subscription/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // Get subscription history
  getSubscriptionHistory: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscription-history/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch subscription history');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }
};

// Notifications API
export const notificationsAPI = {
  getNotifications: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications/`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch notifications');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  markAsRead: async (notificationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications/${notificationId}/mark-read/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to mark notification as read');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  markAllAsRead: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications/mark-all-read/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to mark all notifications as read');
      }

      return data;
    } catch (error) {
      throw error;
    }
  }
};